import { useEffect, useState } from 'react';
import api from '@/lib/api';

const HUMAN_ROLES = ['admin', 'manager', 'scientist', 'pm-readonly'];
const RETIRED = new Set(['colony_traceability_v1', 'yeast_library_materials_v1']);
const ADMIN_ONLY = new Set([
  'experiment_number_repairs_v1', 'azure_analysis_trial_v1', 'historical_analysis_testing_v1',
  'admin_protocol_amendment_v1', 'admin_experiment_completion_v1',
]);

/** Active role, with Scientist view counted as Scientist. */
export function viewedRole(user) {
  return user?.ui_preview === 'scientist' ? 'scientist' : user?.role;
}

/** Code-default Admin privilege. Scientist view does not keep it. */
export function preservedAdmin(user) {
  return viewedRole(user) === 'admin';
}

/** Live grant for Admin, Manager and Scientist. pm-readonly stays out. */
export function labWriter(user) {
  return ['admin', 'manager', 'scientist'].includes(viewedRole(user));
}

/** Live grant for applying a task template: Admin and Manager. */
export function scheduleTemplateWriter(user) {
  return viewedRole(user) === 'admin' || viewedRole(user) === 'manager';
}

export function featureReleased(user, id, feature) {
  if (!user || !HUMAN_ROLES.includes(user.ui_preview === 'scientist' ? 'scientist' : user.role)) return false;
  const retired = feature ? feature.retired : RETIRED.has(id);
  const adminOnly = feature ? feature.admin_only : ADMIN_ONLY.has(id);
  if (retired) return false;
  if (adminOnly) return preservedAdmin(user);
  return true;
}

export function useFeatureAccess(user) {
  const identity = `${user?.id || user?.email || ''}:${user?.role || ''}:${user?.ui_preview || ''}`;
  const [state, setState] = useState(null);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const reload = () => setRefresh(value => value + 1);
    window.addEventListener('labop-feature-access-changed', reload);
    window.addEventListener('focus', reload);
    return () => { window.removeEventListener('labop-feature-access-changed', reload); window.removeEventListener('focus', reload); };
  }, []);
  useEffect(() => {
    if (!user) return undefined;
    let active = true;
    Promise.resolve().then(() => api.get('/feature-access')).then(({ data }) => {
      if (active && Array.isArray(data.features)) setState({ identity, features: data.features });
    }).catch(() => { if (active) setState(null); });
    return () => { active = false; };
  }, [identity, refresh]); // eslint-disable-line react-hooks/exhaustive-deps
  const allowed = id => {
    const feature = state?.identity === identity && state.features.find(item => item.id === id);
    return featureReleased(user, id, feature || null);
  };
  return { allowed, loaded: state?.identity === identity };
}
